import type { TransportRouteSummaryDto } from '../../types/transport';
import {
  buildTransportRouteLabel,
  extractTransportRouteEndpoint,
} from '../orders/transportAddressUtils';
import type { TransportTruckGroup } from './transportOrderGroups';

/** Route header of the grouped list; its rows are trucks, never orders. */
export interface TransportRouteSection extends RouteIdentity {
  trucks: TransportTruckGroup[];
  /** Distinct customer orders carried by the trucks of this section. */
  orderCount: number;
}

export interface TransportOnlyRouteSection extends RouteIdentity {
  transports: TransportRouteSummaryDto[];
}

interface RouteIdentity {
  key: string;
  label: string;
  tooltip: string;
  loadingPointCount: number;
  deliveryPointCount: number;
}

function normalizeEndpoint(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase('pl-PL')
    .replace(/\s+/g, ' ')
    .trim();
}

function endpointSequence(addresses: string[], fallback?: string | null): string[] {
  const endpoints = addresses
    .map((address) => extractTransportRouteEndpoint(address))
    .map((endpoint) => endpoint.trim())
    .filter(Boolean);

  if (endpoints.length > 0) return endpoints;
  const fallbackEndpoint = extractTransportRouteEndpoint(fallback || undefined).trim();
  return fallbackEndpoint ? [fallbackEndpoint] : [];
}

function compactSequence(endpoints: string[], fallback: string): string {
  if (endpoints.length === 0) return fallback;
  if (endpoints.length <= 2) return endpoints.join(' + ');
  return `${endpoints[0]} +${endpoints.length - 1}`;
}

export function getTransportRouteIdentity(
  transport: TransportRouteSummaryDto,
): RouteIdentity {
  const loadingEndpoints = endpointSequence(
    transport.loadingAddresses,
    transport.originCity,
  );
  const deliveryEndpoints = endpointSequence(
    transport.deliveryAddresses,
    transport.destinationCity,
  );
  const route = buildTransportRouteLabel(
    transport.loadingAddresses,
    transport.deliveryAddresses,
    transport.originCity,
    transport.destinationCity,
  );
  const origin = compactSequence(loadingEndpoints, route.origin);
  const destination = compactSequence(deliveryEndpoints, route.destination);

  return {
    key: JSON.stringify({
      loading: loadingEndpoints.map(normalizeEndpoint),
      delivery: deliveryEndpoints.map(normalizeEndpoint),
    }),
    label: origin === '-' && destination === '-' ? '-' : `${origin} → ${destination}`,
    tooltip: route.tooltip,
    loadingPointCount: transport.loadingAddresses.length || (loadingEndpoints.length > 0 ? 1 : 0),
    deliveryPointCount: transport.deliveryAddresses.length || (deliveryEndpoints.length > 0 ? 1 : 0),
  };
}

export function buildTransportRouteSections(
  truckGroups: TransportTruckGroup[],
): TransportRouteSection[] {
  const sections = new Map<string, TransportRouteSection>();
  const orderIdsBySection = new Map<string, Set<string>>();

  truckGroups.forEach((truckGroup) => {
    const identity = getTransportRouteIdentity(truckGroup.transport);
    let section = sections.get(identity.key);
    if (!section) {
      section = { ...identity, trucks: [], orderCount: 0 };
      sections.set(identity.key, section);
      orderIdsBySection.set(identity.key, new Set());
    }
    section.trucks.push(truckGroup);
    truckGroup.orders.forEach(({ orderId }) => orderIdsBySection.get(identity.key)?.add(orderId));
  });

  sections.forEach((section) => {
    section.orderCount = orderIdsBySection.get(section.key)?.size || 0;
  });

  return Array.from(sections.values());
}

export function buildTransportOnlyRouteSections(
  transports: TransportRouteSummaryDto[],
): TransportOnlyRouteSection[] {
  const sections = new Map<string, TransportOnlyRouteSection>();

  transports.forEach((transport) => {
    const identity = getTransportRouteIdentity(transport);
    const section = sections.get(identity.key);
    if (section) {
      section.transports.push(transport);
      return;
    }
    sections.set(identity.key, { ...identity, transports: [transport] });
  });

  return Array.from(sections.values());
}
